<template>
  <!-- วันปิดร้าน: shops that receive the goods themselves, by SAP customer code,
       and the weekday(s) each is closed. The route planner reads it
       (upcountryPlan.closedMap): a listed customer gets shop hours, and a drop
       on its closed day is avoided or flagged. Delivery keeps the list. -->
  <div class="mt-6">
    <div class="d-flex align-center flex-wrap" style="gap: 8px">
      <!-- Collapsed by default: reference data, edited now and then, and ~50 rows. -->
      <h3 class="toggle" @click="toggle"><v-icon>{{ open ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>วันปิดร้าน</h3>
      <span class="caption">{{ rows.length }} ร้าน</span>
      <v-spacer />
      <template v-if="open">
        <v-text-field v-model="search" dense hide-details outlined clearable label="ค้นหารหัส / ชื่อร้าน" style="max-width: 260px" />
        <v-btn small color="primary" @click="openEdit(null)">+ เพิ่มร้าน</v-btn>
      </template>
    </div>
    <v-simple-table v-if="open" dense class="mt-2">
      <thead><tr><th>รหัสลูกค้า SAP</th><th>ชื่อร้าน</th><th>วันปิด</th><th>หมายเหตุ</th><th>แก้ไขล่าสุด</th><th></th></tr></thead>
      <tbody>
        <tr v-if="!shown.length"><td colspan="6" class="grey--text">ยังไม่มีร้านในรายการ</td></tr>
        <tr v-for="r in shown" :key="r.cardCode">
          <td>{{ r.cardCode }}</td>
          <td>{{ r.cardName }}</td>
          <td>{{ P.daysText(r.closedDays) || 'เปิดทุกวัน' }}</td>
          <td>{{ r.note }}</td>
          <td class="caption">{{ $personName(r.updatedBy) }} {{ (r.updatedAt || '').slice(0, 10) }}</td>
          <td class="text-no-wrap">
            <v-btn x-small text @click="openEdit(r)">แก้ไข</v-btn>
            <v-btn x-small text color="error" @click="remove(r)">ลบ</v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <v-dialog v-model="edit.open" max-width="480">
      <v-card>
        <v-card-title>{{ edit.existing ? 'แก้ไขวันปิดร้าน' : 'เพิ่มร้าน' }}</v-card-title>
        <v-card-text>
          <v-autocomplete v-if="!edit.existing" v-model="edit.pick" :items="customers" item-text="label" return-object
            dense outlined clearable label="เลือกจากลูกค้าในออเดอร์รถใหญ่" @change="onPick" />
          <v-text-field v-model="edit.cardCode" :disabled="edit.existing" label="รหัสลูกค้า SAP (CardCode)" dense outlined />
          <v-text-field v-model="edit.cardName" label="ชื่อร้าน" dense outlined />
          <div class="caption mb-1">วันปิดร้าน (ไม่เลือกเลย = เปิดทุกวัน แต่รับของตามเวลาร้าน)</div>
          <div class="d-flex flex-wrap" style="gap: 4px 12px">
            <v-checkbox v-for="(d, i) in P.DOW_TH" :key="i" v-model="edit.closedDays" :value="i" :label="d" dense hide-details class="mt-0" />
          </div>
          <v-text-field v-model="edit.note" label="หมายเหตุ" dense outlined class="mt-3" />
          <v-alert v-if="problem" dense type="error">{{ problem }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="edit.open = false">ยกเลิก</v-btn>
          <v-btn color="primary" :loading="edit.saving" @click="save">บันทึก</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import P from '@/components/upcountryPlan'
import R from '@/components/bookingRules'
import U from '@/components/upcountry'
import US from '@/components/upcountryService'
import { reportError } from '@/components/report'

const OPEN_KEY = 'upcountry.shopClosedOpen'

const blank = () => ({ open: false, existing: false, pick: null, cardCode: '', cardName: '', closedDays: [], note: '', saving: false })

export default {
  name: 'shopClosedDays',
  props: {
    rows: { type: Array, default: () => [] },
    orders: { type: Array, default: () => [] },
    user: { type: String, default: '' }
  },
  data () {
    let open = false
    try { open = localStorage.getItem(OPEN_KEY) === '1' } catch (e) { /* no storage: start collapsed */ }
    return { P, open, search: '', problem: '', edit: blank() }
  },
  computed: {
    shown () {
      const q = String(this.search || '').trim().toLowerCase()
      return q ? this.rows.filter(r => (r.cardCode + ' ' + r.cardName).toLowerCase().indexOf(q) !== -1) : this.rows
    },
    // Every SAP customer on an up-country order, once: the quick way to add one.
    customers () {
      const seen = {}
      const out = []
      this.orders.forEach(o => {
        const code = String(o.cardCode || '').trim()
        if (!code || seen[code]) return
        seen[code] = true
        const name = o.cardName || o.name || ''
        out.push({ cardCode: code, cardName: name, label: `${code} · ${name}` })
      })
      return out.sort((a, b) => a.label.localeCompare(b.label, 'th'))
    }
  },
  methods: {
    toggle () {
      this.open = !this.open
      try { localStorage.setItem(OPEN_KEY, this.open ? '1' : '0') } catch (e) { /* per-viewer convenience only */ }
    },
    openEdit (r) {
      this.problem = ''
      this.edit = Object.assign(blank(), r
        ? { open: true, existing: true, cardCode: r.cardCode, cardName: r.cardName, closedDays: (r.closedDays || []).slice(), note: r.note || '' }
        : { open: true })
    },
    onPick (c) {
      if (!c) return
      this.edit.cardCode = c.cardCode
      this.edit.cardName = c.cardName
    },
    async save () {
      const check = P.checkShopClosedDay(this.edit)
      if (!check.ok) { this.problem = R.errorMessage(check.reason); return }
      if (!this.edit.existing && this.rows.some(r => r.cardCode === check.cardCode)) {
        this.problem = 'มีร้านนี้ในรายการแล้ว — กด แก้ไข ที่แถวของร้าน'
        return
      }
      this.edit.saving = true
      try {
        await US.setShopClosedDay({ cardCode: this.edit.cardCode, cardName: this.edit.cardName, closedDays: this.edit.closedDays, note: this.edit.note, user: this.user })
        this.edit.open = false
      } catch (e) {
        reportError('shopClosedDay save', e)
        this.problem = U.actionMessage(e)
      } finally {
        this.edit.saving = false
      }
    },
    async remove (r) {
      const ok = await this.$root.$message('ลบร้าน', [`${r.cardCode} · ${r.cardName}`, 'แผนเส้นทางจะถือว่าลูกค้ารายนี้รับของได้ทุกวัน ตามเวลาของบ้าน'],
        { type: 'confirm', color: 'warning', width: 420 })
      if (!ok) return
      try {
        await US.deleteShopClosedDay(r.cardCode)
      } catch (e) {
        reportError('shopClosedDay delete', e)
        await this.$root.$message('ลบไม่สำเร็จ', [U.actionMessage(e)], { type: 'alert', color: 'error', width: 420 })
      }
    }
  }
}
</script>

<style scoped>
.toggle {
  cursor: pointer;
  user-select: none;
}
</style>
